import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  EmptyState,
  GlassTable,
  ResponsiveDataTable,
  SkeletonList,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import {
  EXPENSE_STATUSES,
  filter_by_status,
  resolve_expenses_view,
  sum_amounts_by_currency,
} from './expense_utils';
import { EXPENSE_STATUS_KEY } from '../../lib/label_keys';
import { useExpenses } from '../../data/use_expenses';
import type { ExpenseActionFailure } from '../../data/use_expenses';
import { format_money } from '../../lib/format';
import { navigate } from '../../lib/router';
import type { ExpenseTab } from '../../lib/router';
import type { Expense } from '../../data/types';
import { ExpenseRejectDialog } from './expense_reject_dialog';
import { ExpenseMobileCard, ExpensesTableRow } from './expenses_table_row';

/** One row's most recent approve/reject failure, rendered inline beneath its own action buttons — never a raw error toast. */
interface RowActionError {
  claim_id: string;
  failure: ExpenseActionFailure;
}

/** Expense claim queues with inline approve/reject on the pending tab (#22792 — wired to the real `expenseClaim` backend). */
export function ExpensesScreen({
  tab,
}: {
  tab: ExpenseTab;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { state, counts, approve, reject, retry } = useExpenses();
  const [approving_id, set_approving_id] = useState<string | null>(null);
  const [row_error, set_row_error] = useState<RowActionError | null>(null);
  const [reject_target, set_reject_target] = useState<Expense | null>(null);

  const expenses = state.status === 'ready' ? state.expenses : [];
  const rows = filter_by_status(expenses, tab);
  const view = resolve_expenses_view({
    state,
    filtered_row_count: rows.length,
  });
  const can_act = approve !== undefined && reject !== undefined;

  const handle_approve = (expense: Expense): void => {
    if (!approve) return;
    set_row_error(null);
    set_approving_id(expense.id);
    void approve(expense.id)
      .then((result) => {
        if (result.outcome === 'failure') {
          set_row_error({ claim_id: expense.id, failure: result.failure });
        }
      })
      .finally(() => {
        set_approving_id(null);
      });
  };

  return (
    <>
      <ScreenHeader
        title={t('expenses.title')}
        description={t('expenses.subtitle')}
        actions={
          <Button variant="secondary" size="sm">
            {t('common.export')}
          </Button>
        }
      />

      <Tabs
        value={tab}
        onValueChange={(next) => {
          navigate({ module: 'expenses', tab: next as ExpenseTab });
        }}
      >
        <TabsList className="wp-tabstrip mb-3.5">
          {EXPENSE_STATUSES.map((status) => (
            <TabsTrigger key={status} value={status}>
              {t(EXPENSE_STATUS_KEY[status])}{' '}
              <span className="ms-1 text-[11px] text-[color:var(--ink-muted)] num">
                {counts[status]}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {view === 'loading' && (
        <GlassTable>
          <div className="p-4">
            <span className="sr-only">{t('expenses.loading')}</span>
            <SkeletonList rows={5} />
          </div>
        </GlassTable>
      )}

      {view === 'no_entity' && (
        <EmptyState
          title={t('expenses.no_entity_title')}
          description={t('expenses.no_entity_description')}
        />
      )}

      {view === 'entitlement_required' && (
        <EmptyState
          title={t('expenses.entitlement_required_title')}
          description={t('expenses.entitlement_required_description')}
        />
      )}

      {view === 'error' && (
        <EmptyState
          title={t('expenses.load_error_title')}
          description={t('expenses.load_error_description')}
          action={
            <Button variant="secondary" onClick={retry}>
              {t('expenses.retry')}
            </Button>
          }
        />
      )}

      {view === 'empty' && (
        <EmptyState
          title={t('expenses.empty_title')}
          description={t('expenses.empty_description')}
        />
      )}

      {view === 'rows' &&
        (() => {
          // Computed once, rendered in BOTH the desktop footer (inside
          // GlassTable, outside its own overflow-x-auto wrapper -- always
          // visible pre-adoption) and the phone-only totals line below --
          // same source array, same `sum_amounts_by_currency` call, never
          // duplicated math (adversarial review, #23618: the desktop-only
          // `renderDesktop` leg silently dropped the #22792 totals on
          // phones; this restores them for the card presentation too).
          const totals_lines = sum_amounts_by_currency(rows).map(
            ({ currency, total }) => (
              <span key={currency} className="num">
                {t('expenses.total', {
                  amount: format_money(total, currency, locale),
                })}
              </span>
            ),
          );

          return (
            <>
              <ResponsiveDataTable<Expense>
                data-testid="expenses-table"
                rows={rows}
                rowKey={(expense) => expense.id}
                // Below `md` the desktop table's last column (approve/reject +
                // the inline action-failure paragraph) is off-screen behind
                // horizontal scroll -- `ExpenseMobileCard` renders both directly
                // in the card body instead. See its own docblock.
                mobileCard={(expense) => (
                  <ExpenseMobileCard
                    expense={expense}
                    locale={locale}
                    can_act={can_act}
                    is_approving={approving_id === expense.id}
                    action_failure={
                      row_error?.claim_id === expense.id
                        ? row_error.failure
                        : undefined
                    }
                    on_approve={() => {
                      handle_approve(expense);
                    }}
                    on_reject_click={() => {
                      set_row_error(null);
                      set_reject_target(expense);
                    }}
                  />
                )}
                // Desktop leg kept byte-identical to the pre-adoption markup
                // (GlassTable + per-currency footer totals) via the
                // `renderDesktop` escape hatch -- see responsive_data_table.tsx.
                renderDesktop={(rows) => (
                  <GlassTable
                    footer={
                      <div className="flex flex-col items-end gap-1 text-[13px] font-semibold">
                        {/* One total PER CURRENCY, never a single sum across mixed
                          currencies (adversarial review, #22792) -- a queue can
                          genuinely hold more than one currency pre-approval. */}
                        {totals_lines}
                      </div>
                    }
                  >
                    <table className="nv-table">
                      <thead>
                        <tr>
                          <th scope="col">{t('expenses.column_employee')}</th>
                          <th scope="col">
                            {t('expenses.column_claim_number')}
                          </th>
                          <th scope="col" className="text-end">
                            {t('expenses.column_amount')}
                          </th>
                          <th scope="col">{t('expenses.column_submitted')}</th>
                          <th scope="col">
                            {t('expenses.column_reimbursement')}
                          </th>
                          <th scope="col">
                            <span className="sr-only">
                              {t('common.actions')}
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((expense) => (
                          <ExpensesTableRow
                            key={expense.id}
                            expense={expense}
                            locale={locale}
                            can_act={can_act}
                            is_approving={approving_id === expense.id}
                            action_failure={
                              row_error?.claim_id === expense.id
                                ? row_error.failure
                                : undefined
                            }
                            on_approve={() => {
                              handle_approve(expense);
                            }}
                            on_reject_click={() => {
                              set_row_error(null);
                              set_reject_target(expense);
                            }}
                          />
                        ))}
                      </tbody>
                    </table>
                  </GlassTable>
                )}
              />
              {/* Phone-only totals line (adversarial review, PR #23618): the
                desktop leg's per-currency totals (#22792) render inside
                GlassTable's footer, OUTSIDE its own overflow-x-auto wrapper,
                so they stay visible on a 360px viewport pre-adoption. The
                whole GlassTable now lives inside `renderDesktop`'s
                `hidden md:block` leg, so without this the card list would
                render with no totals at all -- reusing the exact same
                `totals_lines` computed above, never re-derived. */}
              {totals_lines.length > 0 && (
                <div
                  className="md:hidden mt-3 flex flex-col items-end gap-1 text-[13px] font-semibold"
                  data-testid="expenses-mobile-totals"
                >
                  {totals_lines}
                </div>
              )}
            </>
          );
        })()}

      <ExpenseRejectDialog
        expense={reject_target}
        onOpenChange={(open) => {
          if (!open) set_reject_target(null);
        }}
        reject={reject}
        onFailure={(failure) => {
          if (reject_target) {
            set_row_error({ claim_id: reject_target.id, failure });
          }
        }}
      />
    </>
  );
}
